import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Save, Trash2, MousePointer2, Spline, Circle, Square, Diamond, OctagonX } from "lucide-react";

const SHAPES = [
  { type: "start", label: "Start", icon: Circle },
  { type: "process", label: "Process", icon: Square },
  { type: "decision", label: "Decision", icon: Diamond },
  { type: "end", label: "End", icon: OctagonX },
];
const SIZE = { start: [120, 44], process: [150, 52], decision: [140, 84], end: [120, 44] };
const uid = () => Math.random().toString(36).slice(2, 10);

function NodeShape({ n, selected, connectFrom }) {
  const [w, h] = SIZE[n.type];
  const stroke = selected ? "#1E6FE8" : connectFrom ? "#CC8F00" : "#525252";
  const sw = selected || connectFrom ? 2.5 : 1.5;
  const common = { stroke, strokeWidth: sw, fill: "#fff" };
  if (n.type === "decision")
    return <polygon points={`${n.x},${n.y - h / 2} ${n.x + w / 2},${n.y} ${n.x},${n.y + h / 2} ${n.x - w / 2},${n.y}`} {...common} fill="#FEF9EC" />;
  if (n.type === "start" || n.type === "end")
    return <rect x={n.x - w / 2} y={n.y - h / 2} width={w} height={h} rx={h / 2} {...common} fill={n.type === "start" ? "#ECFDF5" : "#FEF2F2"} />;
  return <rect x={n.x - w / 2} y={n.y - h / 2} width={w} height={h} {...common} />;
}

function edgeEnds(a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len, uy = dy / len;
  const rA = (SIZE[a.type][0] / 2 + SIZE[a.type][1] / 2) / 2;
  const rB = (SIZE[b.type][0] / 2 + SIZE[b.type][1] / 2) / 2;
  return { x1: a.x + ux * rA, y1: a.y + uy * rA, x2: b.x - ux * (rB + 6), y2: b.y - uy * (rB + 6) };
}

export default function ProcessMapPage() {
  const { projectId } = useParams();
  const svgRef = useRef(null);
  const [maps, setMaps] = useState([]);
  const [active, setActive] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("select");
  const [connectFrom, setConnectFrom] = useState(null);
  const [drag, setDrag] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = () => api.get(`/projects/${projectId}/artifacts/process_maps`).then((r) => {
    setMaps(r.data);
    if (r.data.length && !active) openMap(r.data[0]);
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [projectId]);

  const openMap = (m) => { setActive(m); setNodes(m.nodes || []); setEdges(m.edges || []); setSelected(null); setConnectFrom(null); setDirty(false); };

  const createMap = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/artifacts/process_maps`, {
        name: `Process map ${maps.length + 1}`,
        nodes: [
          { id: uid(), type: "start", label: "Start", x: 140, y: 90 },
          { id: uid(), type: "process", label: "Step 1", x: 360, y: 90 },
        ],
        edges: [],
      });
      setMaps([...maps, data]); openMap(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveMap = async () => {
    try {
      await api.put(`/projects/${projectId}/artifacts/process_maps/${active.id}`, { name: active.name, nodes, edges });
      setDirty(false); toast.success("Process map saved");
      setMaps(maps.map((m) => (m.id === active.id ? { ...m, name: active.name, nodes, edges } : m)));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteMap = async (m) => {
    try {
      await api.delete(`/projects/${projectId}/artifacts/process_maps/${m.id}`);
      const rest = maps.filter((x) => x.id !== m.id);
      setMaps(rest);
      if (active?.id === m.id) rest.length ? openMap(rest[0]) : (setActive(null), setNodes([]), setEdges([]));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addNode = (type) => {
    const i = nodes.length;
    const n = { id: uid(), type, label: SHAPES.find((s) => s.type === type).label, x: 160 + (i % 5) * 190, y: 220 + Math.floor(i / 5) * 110 };
    setNodes([...nodes, n]); setSelected({ kind: "node", id: n.id }); setDirty(true);
  };

  const svgPoint = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onNodePointerDown = (e, n) => {
    e.stopPropagation();
    if (mode === "connect") {
      if (!connectFrom) { setConnectFrom(n.id); return; }
      if (connectFrom !== n.id && !edges.some((ed) => ed.from === connectFrom && ed.to === n.id)) {
        setEdges([...edges, { id: uid(), from: connectFrom, to: n.id, label: "" }]);
        setDirty(true);
      }
      setConnectFrom(null);
      return;
    }
    const p = svgPoint(e);
    setSelected({ kind: "node", id: n.id });
    setDrag({ id: n.id, ox: p.x - n.x, oy: p.y - n.y });
  };

  const onMove = (e) => {
    if (!drag) return;
    const p = svgPoint(e);
    setNodes((ns) => ns.map((n) => (n.id === drag.id ? { ...n, x: Math.max(30, p.x - drag.ox), y: Math.max(30, p.y - drag.oy) } : n)));
    setDirty(true);
  };

  const deleteSelected = () => {
    if (!selected) return;
    if (selected.kind === "node") {
      setNodes(nodes.filter((n) => n.id !== selected.id));
      setEdges(edges.filter((e) => e.from !== selected.id && e.to !== selected.id));
    } else setEdges(edges.filter((e) => e.id !== selected.id));
    setSelected(null); setDirty(true);
  };

  const selNode = selected?.kind === "node" ? nodes.find((n) => n.id === selected.id) : null;
  const selEdge = selected?.kind === "edge" ? edges.find((e) => e.id === selected.id) : null;
  const setLabel = (v) => {
    if (selNode) setNodes(nodes.map((n) => (n.id === selNode.id ? { ...n, label: v } : n)));
    if (selEdge) setEdges(edges.map((e) => (e.id === selEdge.id ? { ...e, label: v } : e)));
    setDirty(true);
  };

  return (
    <div data-testid="process-map-page">
      <div className="flex items-start justify-between">
        <PageHeader overline="Six Sigma Toolkit · Click & drag flowcharting" title="Process Map" />
        <Button data-testid="pm-new-map-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={createMap}>
          <Plus className="h-4 w-4 mr-1" /> New map
        </Button>
      </div>

      <div className="flex gap-4 mt-4">
        <div className="w-52 shrink-0">
          <div className="overline-label mb-2">Maps</div>
          {maps.map((m) => (
            <div key={m.id} data-testid="pm-map-item"
              className={`flex items-center justify-between border px-3 py-2 mb-1 cursor-pointer text-sm ${active?.id === m.id ? "border-[#1E6FE8] bg-[#1E6FE8]/5 font-semibold" : "border-border bg-card"}`}
              onClick={() => openMap(m)}>
              <span className="truncate">{m.name}</span>
              <button data-testid="pm-delete-map-btn" onClick={(e) => { e.stopPropagation(); deleteMap(m); }} className="text-muted-foreground hover:text-red-600 ml-2"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {maps.length === 0 && <div className="text-xs text-muted-foreground">No maps yet — create one.</div>}
        </div>

        <div className="flex-1 min-w-0">
          {active ? (
            <>
              <div className="flex flex-wrap items-center gap-2 border border-border bg-card p-2 mb-2">
                <Input data-testid="pm-map-name-input" className="w-48 h-8" value={active.name}
                  onChange={(e) => { setActive({ ...active, name: e.target.value }); setDirty(true); }} />
                <span className="w-px h-6 bg-border" />
                {SHAPES.map((s) => (
                  <Button key={s.type} data-testid={`pm-add-${s.type}-btn`} variant="outline" size="sm" className="rounded-none h-8" onClick={() => addNode(s.type)}>
                    <s.icon className="h-3.5 w-3.5 mr-1" /> {s.label}
                  </Button>
                ))}
                <span className="w-px h-6 bg-border" />
                <Button data-testid="pm-mode-select-btn" variant={mode === "select" ? "default" : "outline"} size="sm" className="rounded-none h-8"
                  onClick={() => { setMode("select"); setConnectFrom(null); }}><MousePointer2 className="h-3.5 w-3.5 mr-1" /> Select</Button>
                <Button data-testid="pm-mode-connect-btn" variant={mode === "connect" ? "default" : "outline"} size="sm" className="rounded-none h-8"
                  onClick={() => setMode("connect")}><Spline className="h-3.5 w-3.5 mr-1" /> Connect</Button>
                {selected && (
                  <>
                    <Input data-testid="pm-label-input" className="w-40 h-8" placeholder={selEdge ? "Edge label (Yes/No)" : "Label"}
                      value={(selNode?.label ?? selEdge?.label) || ""} onChange={(e) => setLabel(e.target.value)} />
                    <Button data-testid="pm-delete-selected-btn" variant="outline" size="sm" className="rounded-none h-8 text-red-600" onClick={deleteSelected}>
                      <Trash2 className="h-3.5 w-3.5" /></Button>
                  </>
                )}
                <div className="flex-1" />
                <Button data-testid="pm-save-btn" size="sm" className="rounded-none h-8 bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={saveMap} disabled={!dirty}>
                  <Save className="h-3.5 w-3.5 mr-1" /> {dirty ? "Save" : "Saved"}
                </Button>
              </div>
              {mode === "connect" && (
                <div data-testid="pm-connect-hint" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 mb-2">
                  Connect mode: click a source shape, then a target shape to draw an arrow.{connectFrom ? " Source selected — now click the target." : ""}
                </div>
              )}
              <svg ref={svgRef} data-testid="pm-canvas" className="w-full border border-border bg-white select-none" style={{ height: 560, touchAction: "none" }}
                onPointerMove={onMove} onPointerUp={() => setDrag(null)} onPointerLeave={() => setDrag(null)}
                onPointerDown={() => { setSelected(null); if (mode === "connect") setConnectFrom(null); }}>
                <defs>
                  <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#525252" />
                  </marker>
                  <marker id="arrow-sel" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#1E6FE8" />
                  </marker>
                  <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="1" fill="#E5E5E5" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
                {edges.map((e) => {
                  const a = nodes.find((n) => n.id === e.from), b = nodes.find((n) => n.id === e.to);
                  if (!a || !b) return null;
                  const { x1, y1, x2, y2 } = edgeEnds(a, b);
                  const sel = selected?.kind === "edge" && selected.id === e.id;
                  return (
                    <g key={e.id} data-testid="pm-edge" className="cursor-pointer"
                      onPointerDown={(ev) => { ev.stopPropagation(); setSelected({ kind: "edge", id: e.id }); }}>
                      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth="20" />
                      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={sel ? "#1E6FE8" : "#525252"} strokeWidth={sel ? 2.5 : 1.5}
                        markerEnd={`url(#${sel ? "arrow-sel" : "arrow"})`} />
                      {e.label && (
                        <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 6} textAnchor="middle" fontSize="11" fill="#CC8F00" fontWeight="600"
                          style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}>{e.label}</text>
                      )}
                    </g>
                  );
                })}
                {nodes.map((n) => (
                  <g key={n.id} data-testid={`pm-node-${n.type}`} className="cursor-grab active:cursor-grabbing"
                    onPointerDown={(e) => onNodePointerDown(e, n)}>
                    <NodeShape n={n} selected={selected?.kind === "node" && selected.id === n.id} connectFrom={connectFrom === n.id} />
                    <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize="12" fontWeight="600" fill="#171717" pointerEvents="none">
                      {n.label.length > 20 ? n.label.slice(0, 19) + "…" : n.label}
                    </text>
                  </g>
                ))}
              </svg>
              <p className="text-xs text-muted-foreground mt-2">Drag shapes to arrange · select a shape or arrow to rename/delete it · use Connect to draw arrows · label decision arrows Yes / No.</p>
            </>
          ) : (
            <div className="border border-dashed border-border p-12 text-center text-muted-foreground text-sm">Create your first process map to start diagramming.</div>
          )}
        </div>
      </div>
    </div>
  );
}
